import React, { useEffect, useState } from 'react';
import { createBooking, getAvailableSlots } from '../api/visitBookingApi';
import './VisitBookingModal.css';

const BOOKING_WINDOW_DAYS = 30;

// Builds a local (not UTC) YYYY-MM-DD string — toISOString() would shift the date
// for users in timezones ahead of UTC during early-morning local hours.
const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatTime12h = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const formatDateLabel = (isoDate) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const VisitBookingModal = ({ onClose }) => {
  const today = new Date();
  const minDate = toDateInputValue(today);
  const maxDate = toDateInputValue(new Date(today.getFullYear(), today.getMonth(), today.getDate() + BOOKING_WINDOW_DAYS));

  const [selectedDate, setSelectedDate] = useState(minDate);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(null);

  const loadSlots = (date) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    getAvailableSlots(date)
      .then((res) => setSlots(res.data.available || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  useEffect(() => { loadSlots(selectedDate); }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await createBooking({ name, companyName, email, phone, date: selectedDate, slotStartTime: selectedSlot });
      setConfirmed(res.data);
    } catch (err) {
      if (err.response?.status === 409) {
        setError("That slot was just taken — pick another.");
        loadSlots(selectedDate);
      } else {
        setError(err.response?.data?.error || 'Something went wrong — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vbm-backdrop" onClick={onClose} role="presentation">
      <div className="vbm-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="vbm-close" onClick={onClose} aria-label="Close">×</button>

        {confirmed ? (
          <div className="vbm-success">
            <div className="vbm-success__icon">✓</div>
            <h3>You're booked!</h3>
            <p>
              {formatDateLabel(confirmed.visitDate)} at {formatTime12h(confirmed.slotStartTime)}
            </p>
            <p className="vbm-success__note">A confirmation email has been sent to {confirmed.companyEmail}.</p>
            <button type="button" className="vbm-primary-btn" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <h3 className="vbm-title">Book a Visit to Our Experience Center</h3>
            <p className="vbm-subtitle">Hyderabad · 30-minute slots, 10am–6pm, Monday–Saturday</p>

            <label className="vbm-label">Choose a date</label>
            <input
              type="date"
              className="vbm-date-input"
              value={selectedDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />

            <label className="vbm-label">Choose a time</label>
            {loadingSlots ? (
              <p className="vbm-status">Loading slots…</p>
            ) : slots.length === 0 ? (
              <p className="vbm-status">No slots available that day — try another date.</p>
            ) : (
              <div className="vbm-slot-grid">
                {slots.map((slot) => (
                  <button
                    type="button" key={slot}
                    className={`vbm-slot${selectedSlot === slot ? ' is-selected' : ''}`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {formatTime12h(slot)}
                  </button>
                ))}
              </div>
            )}

            {selectedSlot && (
              <form onSubmit={handleSubmit} className="vbm-form">
                {error && <p className="vbm-error">{error}</p>}
                <label className="vbm-label">Your name</label>
                <input className="vbm-input" value={name} onChange={(e) => setName(e.target.value)} required />

                <label className="vbm-label">Company name</label>
                <input className="vbm-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />

                <label className="vbm-label">Company email</label>
                <input type="email" className="vbm-input" value={email} onChange={(e) => setEmail(e.target.value)} required />

                <label className="vbm-label">Phone number</label>
                <input type="tel" className="vbm-input" value={phone} onChange={(e) => setPhone(e.target.value)} required />

                <button type="submit" className="vbm-primary-btn" disabled={submitting}>
                  {submitting ? 'Booking…' : `Confirm ${formatTime12h(selectedSlot)} on ${selectedDate}`}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VisitBookingModal;
